import {SagaIterator} from 'redux-saga';
import {opt} from 'ts-opt';
import {put, take} from 'typed-redux-saga';

import {AssociationType} from 'api/gen/AssociationType';
import {FemaleHolsteinAnimalInList} from 'api/gen/FemaleHolsteinAnimalInList';
import {MaleFleckviehAnimalInList} from 'api/gen/MaleFleckviehAnimalInList';
import {MaleHolsteinAnimalInList} from 'api/gen/MaleHolsteinAnimalInList';
import {Sex} from 'api/gen/Sex';
import {firstPageTableDataRequest} from 'utils/tables';

import {matingAction} from '../action';

/**
 * Updates the animal session after a successful selection flow.
 *
 * @param sex Sex-specific session to update.
 * @param associationType Association type used by the current workflow.
 */
export function* updateAnimalSessionAfterSelection(sex: Sex, associationType: AssociationType): SagaIterator {
    if (sex === 'FEMALE') {
        yield* put(matingAction.getMatingCows(opt({action: 'refresh'})));
        yield* put(matingAction.getAnimalsCount('FEMALE', 'HOLSTEIN'));
        yield* take('mating/SET_FEMALE_ANIMAL_COUNT_IN_SESSION');
        yield* put(matingAction.setFemaleSessionState('selection'));
    } else {
        if (associationType === 'HOLSTEIN') {
            yield* put(matingAction.getHolsteinMatingBulls(opt({action: 'refresh'})));
            yield* put(matingAction.getAnimalsCount('MALE', 'HOLSTEIN'));
        } else {
            yield* put(matingAction.getFleckviehMatingBulls(opt({action: 'refresh'})));
            yield* put(matingAction.getAnimalsCount('MALE', 'FLECKVIEH'));
        }
        yield* put(matingAction.setMaleSessionState('selection'));
    }
}

/**
 * Updates the animal session after a successful filter flow.
 *
 * @param sex Sex-specific session to update.
 * @param associationType Association type used by the current workflow.
 * @param isFull Whether the backend reports that the session is already full.
 */
export function* updateAnimalSessionAfterFilter(
    sex: Sex,
    associationType: AssociationType,
    isFull: boolean,
): SagaIterator {
    if (sex === 'FEMALE') {
        // Filtering replaces the session contents, so the previously selected page may no longer exist.
        yield* put(matingAction.getMatingCows(firstPageTableDataRequest<FemaleHolsteinAnimalInList>()));
        yield* put(matingAction.setFemaleAnimalExistInSession(isFull));
        yield* put(matingAction.setFemaleSessionState('filter'));
    } else {
        // Filtering replaces the session contents, so the previously selected page may no longer exist.
        yield* associationType === 'HOLSTEIN'
            ? put(matingAction.getHolsteinMatingBulls(firstPageTableDataRequest<MaleHolsteinAnimalInList>()))
            : put(matingAction.getFleckviehMatingBulls(firstPageTableDataRequest<MaleFleckviehAnimalInList>()));
        yield* put(matingAction.setMaleAnimalExistInSession(isFull));
        yield* put(matingAction.setMaleSessionState('filter'));
    }
}
